import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  FiHeart,
  FiTrendingUp,
  FiCalendar,
  FiArrowLeft,
  FiBarChart2,
  FiChevronRight,
  FiLoader,
  FiAlertCircle,
} from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { getZoneColor } from "../../utils/zoneUtils";

interface MoodEntry {
  id: number;
  date: string;
  time: string;
  mood: string;
  emotion: string;
  zone: string;
  pleasantness: number;
  impact: number;
  note?: string;
}

interface ChildData {
  id: number;
  name: string;
  grade: string;
  school: string;
}

const ChildMoodTracking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedPeriod, setSelectedPeriod] = useState<"week" | "month" | "year">("month");
  const [childData, setChildData] = useState<ChildData | null>(null);
  const [moodEntries, setMoodEntries] = useState<MoodEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalEntries: 0,
    averagePleasantness: 0,
    averageImpact: 0,
    mostCommonZone: "N/A",
    mostCommonMood: "N/A",
  });

  // Calculate date range based on selected period
  const getDateRange = () => {
    const today = new Date();
    const dateFrom = new Date();

    switch (selectedPeriod) {
      case "week":
        dateFrom.setDate(today.getDate() - 7);
        break;
      case "month":
        dateFrom.setMonth(today.getMonth() - 1);
        break;
      case "year":
        dateFrom.setFullYear(today.getFullYear() - 1);
        break;
    }

    return {
      dateFrom: dateFrom.toISOString().split("T")[0],
      dateTo: today.toISOString().split("T")[0],
    };
  };

  // Fetch child data and mood logs
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError("Child ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch student data
        const studentResponse = await axios.get(`${API_BASE_URL}/students/${id}`);
        const student = studentResponse.data;

        setChildData({
          id: student.id,
          name: student.name,
          grade: student.class?.name || "N/A",
          school: student.school?.name || "N/A",
        });

        // Fetch mood logs
        const dateRange = getDateRange();
        const moodLogsResponse = await axios.get(`${API_BASE_URL}/student-mood-logs`, {
          params: {
            studentId: id,
            status: true,
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo,
          },
        });

        const moodLogs = moodLogsResponse.data || [];
        const transformedEntries: MoodEntry[] = moodLogs.map((log: any) => ({
          id: log.id,
          date: log.date,
          time: log.time ? log.time.substring(0, 5) : "00:00",
          mood: log.calculatedEmotion || "N/A",
          emotion: log.calculatedEmotion || "N/A",
          zone: log.calculatedZone ? log.calculatedZone : "N/A",
          pleasantness: log.joyfulness || 0,
          impact: log.impact || 0,
          note: log.addNote || undefined,
        }));

        setMoodEntries(transformedEntries);

        // Calculate stats
        if (transformedEntries.length > 0) {
          const avgPleasantness = Math.round(
            transformedEntries.reduce((sum, e) => sum + e.pleasantness, 0) /
              transformedEntries.length
          );
          const avgImpact = Math.round(
            transformedEntries.reduce((sum, e) => sum + e.impact, 0) /
              transformedEntries.length
          );

          // Find most common zone
          const zoneCounts: { [key: string]: number } = {};
          transformedEntries.forEach((e) => {
            zoneCounts[e.zone] = (zoneCounts[e.zone] || 0) + 1;
          });
          const mostCommonZone =
            Object.keys(zoneCounts).reduce((a, b) =>
              zoneCounts[a] > zoneCounts[b] ? a : b
            ) || "N/A";

          // Find most common mood
          const moodCounts: { [key: string]: number } = {};
          transformedEntries.forEach((e) => {
            moodCounts[e.mood] = (moodCounts[e.mood] || 0) + 1;
          });
          const mostCommonMood =
            Object.keys(moodCounts).reduce((a, b) =>
              moodCounts[a] > moodCounts[b] ? a : b
            ) || "N/A";

          setStats({
            totalEntries: transformedEntries.length,
            averagePleasantness: avgPleasantness,
            averageImpact: avgImpact,
            mostCommonZone,
            mostCommonMood,
          });
        } else {
          setStats({
            totalEntries: 0,
            averagePleasantness: 0,
            averageImpact: 0,
            mostCommonZone: "N/A",
            mostCommonMood: "N/A",
          });
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError("Failed to load mood tracking data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, selectedPeriod]);

  // getZoneColor imported from ../../utils/zoneUtils — single source of truth

  if (loading) {
    return (
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FiLoader className="mx-auto text-[#1ecab8] mb-4 animate-spin" size={48} />
          <p className="text-slate-600">Loading mood tracking data...</p>
        </div>
      </div>
    );
  }

  if (error || !childData) {
    return (
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
        <Link
          to="/parent/children"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1ecab8] mb-4 transition-colors"
        >
          <FiArrowLeft />
          Back to Children
        </Link>
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200/80">
          <FiAlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-red-600 mb-4">{error || "Child not found"}</p>
          <Link
            to="/parent/children"
            className="inline-block px-4 py-2 bg-[#1ecab8] text-white rounded-lg hover:bg-[#1bb8a6] transition-colors"
          >
            Back to Children
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/parent/children"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1ecab8] mb-4 transition-colors"
        >
          <FiArrowLeft />
          Back to Children
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {childData.name}'s Mood Tracking
            </h1>
            <p className="text-slate-600">
              {childData.grade} • {childData.school}
            </p>
          </div>
          <div className="flex gap-2">
            {(["week", "month", "year"] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedPeriod === period
                    ? "bg-[#1ecab8] text-white shadow-md"
                    : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="text-sm text-slate-600 mb-1">Total Entries</div>
          <div className="text-3xl font-bold text-slate-900">{stats.totalEntries}</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="text-sm text-slate-600 mb-1">Avg. Pleasantness</div>
          <div className="text-3xl font-bold text-slate-900">{stats.averagePleasantness}/7</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="text-sm text-slate-600 mb-1">Avg. Impact</div>
          <div className="text-3xl font-bold text-slate-900">{stats.averageImpact}/7</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="text-sm text-slate-600 mb-1">Most Common Zone</div>
          <div className="text-lg font-bold text-slate-900">{stats.mostCommonZone}</div>
        </div>
      </div>

      {/* Mood Entries */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <FiHeart />
            <span>Mood History</span>
          </div>
        </div>
        <div className="space-y-4">
          {moodEntries.length === 0 ? (
            <div className="text-center py-12">
              <FiHeart className="mx-auto text-slate-400 mb-4" size={48} />
              <p className="text-slate-600">No mood entries found for the selected period.</p>
            </div>
          ) : (
            moodEntries.map((entry) => (
            <div
              key={entry.id}
              className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold"
                    style={{ background: getZoneColor(entry.zone).color }}
                  >
                    {entry.mood.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{entry.mood}</h3>
                      <span className="text-sm text-slate-500">•</span>
                      <span className="text-sm text-slate-600">{entry.emotion}</span>
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold text-white"
                        style={{ background: getZoneColor(entry.zone).color }}
                      >
                        {entry.zone}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                      <span className="flex items-center gap-1">
                        <FiCalendar size={14} />
                        {new Date(entry.date).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <span>•</span>
                      <span>{entry.time}</span>
                    </div>
                    {entry.note && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-2">
                        {entry.note}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span>Pleasantness: {entry.pleasantness}/7</span>
                      <span>•</span>
                      <span>Impact: {entry.impact}/7</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildMoodTracking;

